import React, { useState } from 'react'
import { CircularProgress } from '@mui/material'
import { faLightbulb } from '@fortawesome/pro-regular-svg-icons'
import type { FundingTodo } from '@labrav/funding'
import { ActivityFeedRow } from '../../../../oppsync/components/CoSell/FundingChatHeader/ActivityFeed/ActivityFeedRow'
import { useStyles } from '../../../../oppsync/components/CoSell/FundingChatHeader/ActivityFeed/ActivityFeed.styles'

const INITIAL_VISIBLE_COUNT = 4
const MORE_BATCH_COUNT = 10

interface FundingAgentTodosHeaderProps {
  todos: FundingTodo[]
  loading: boolean
  onSelect: (todo: FundingTodo) => void
}

/**
 * The funding chat's `header` slot on Salesforce Canvas: the "ToDos &
 * Recommendations" section only (no funding stat cards). Reuses ActivityFeed's
 * row + styles for a look identical to the funding dashboard, but is fed the
 * opportunity-scoped agent recommendations passed in as a prop rather than the
 * funding summary. Selecting a row is delegated to the host via `onSelect`.
 */
export const FundingAgentTodosHeader: React.FC<
  FundingAgentTodosHeaderProps
> = ({ todos, loading, onSelect }) => {
  const { classes } = useStyles()
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT)

  const visibleTodos = todos.slice(0, visibleCount)
  const nextBatch = Math.min(MORE_BATCH_COUNT, todos.length - visibleCount)
  const canCollapse = visibleCount > INITIAL_VISIBLE_COUNT
  const hasItems = todos.length > 0
  const isInitialLoading = loading && !hasItems

  return (
    <div className={classes.root}>
      <span className={classes.sectionTitle}>ToDos &amp; Recommendations</span>

      {isInitialLoading ? (
        <div className={classes.loadingRow}>
          <CircularProgress size={16} thickness={5} />
        </div>
      ) : !hasItems ? (
        <div className={classes.emptyRow}>
          You&apos;re all caught up — nothing needs action.
        </div>
      ) : (
        <div className={classes.list}>
          {visibleTodos.map(todo => (
            <ActivityFeedRow
              key={todo.id}
              icon={faLightbulb}
              text={todo.text}
              actionLabel="View details"
              onAction={() => onSelect(todo)}
            />
          ))}
          {(nextBatch > 0 || canCollapse) && (
            <div className={classes.viewMoreRow}>
              {nextBatch > 0 && (
                <button
                  className={classes.viewMoreLink}
                  type="button"
                  onClick={() =>
                    setVisibleCount(count => count + MORE_BATCH_COUNT)
                  }
                >
                  View {nextBatch} more out of total {todos.length}
                </button>
              )}
              {canCollapse && (
                <button
                  className={classes.viewMoreLink}
                  type="button"
                  onClick={() => setVisibleCount(INITIAL_VISIBLE_COUNT)}
                >
                  Show less
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default FundingAgentTodosHeader
